import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import LoadingModal from "../components/LoadingModal";
import SearchBar from "../components/SearchBar";
import Button from "../components/Button";
import IcarusServices from "../services/IcarusServices";
import withAuthorization from "../components/withAuthorization";
import { formatCurrency } from "../tools/tool";

const parseCurrencyToNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value).trim();
  if (!raw) return 0;

  const isParenNegative = raw.startsWith("(") && raw.endsWith(")");
  const normalized = (isParenNegative ? raw.slice(1, -1) : raw).replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return 0;
  return isParenNegative ? -Math.abs(parsed) : parsed;
};

const pick = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== "") return value;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString();
};

const IcarusAccounts = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  const limit = 100;
  const [offset, setOffset] = useState(0);

  const columns = useMemo(
    () => [
      {
        name: "Account ID",
        selector: (row) => pick(row, ["accountId"], ""),
        sortable: true,
        wrap: true,
        width: "120px",
      },
      {
        name: "Account Name",
        selector: (row) => pick(row, ["firstName", "first_name", "fname","accountName"], ""),
        sortable: true,
        wrap: true,
        width: "200px",
      },
      {
        name: "Mobile Number",
        selector: (row) => pick(row, ["mobileNumber", "mobile_number", "phone", "phoneNumber","accountNumber"], ""),
        sortable: true,
        wrap: true,
        width: "150px",
      },
      {
        name: "Email Address",
        selector: (row) => pick(row, ["emailAddress", "email_address", "email"], ""),
        sortable: true,
        wrap: true,
        width: "200px",
      },
      {
        name: "Date Created",
        selector: (row) =>
          formatDateTime(
            pick(row, ["dateCreated","datecreated", "date_created", "createdAt", "created_at", "createdDate"], "")
          ),
        sortable: true,
        wrap: true,
        width: "190px",
      },
      {
        name: "Wallet Balance",
        selector: (row) =>
          parseCurrencyToNumber(pick(row, ["wallet_amount", "walletAmount", "wallet_balance"], 0)),
        cell: (row) => {
          const amount = parseCurrencyToNumber(
            pick(row, ["wallet_amount", "walletAmount", "wallet_balance"], 0)
          );
          return formatCurrency(amount);
        },
        ignoreRowClick: true,
        allowOverflow: true,
        sortable: true,
        width: "180px",
        right: true,
        wrap: true,
      },
    ],
    []
  );

  const getAccounts = async () => {
    setLoading(true);
    const form = new FormData();
    form.append("pLimit", limit);
    form.append("pOffset", offset);

    try {
      const response = await IcarusServices.storedProcedure("getAccounts", form);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setData(rows);
      setFilteredData(rows);
    } catch (e) {
      if (e?.status === 401 || e?.response?.status === 401) {
        window.location.href = "/";
        return;
      }
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  return (
    <div className="m-5">
      <LoadingModal show={loading} message={"Loading Accounts"} />

      <div style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: 10 }}>
        Icarus Accounts
      </div>

      <div className="flexRow">
        <div className="flexRow filterDiv">
          <SearchBar
            placeholder="Search Accounts"
            onChange={(e) => {
              const value = e.target.value.toLowerCase();
              const filtered = data.filter((item) =>
                Object.keys(item).some((key) =>
                  item[key] && item[key].toString().toLowerCase().includes(value)
                )
              );
              setFilteredData(filtered);
            }}
          />
        </div>
        <div className="spacer"></div>
          {/* <Button
            variant={"light"}
            size={"xs"}
            onClick={() =>
              setOffset((prev) => {
                const next = prev - limit;
                return next < 0 ? 0 : next;
              })
            }
          >
            Prev
          </Button>
          &nbsp;&nbsp;
          <Button variant={"light"} size={"xs"} onClick={() => setOffset((prev) => prev + limit)}>
            Next
          </Button> */}
      </div>

      <br />

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        highlightOnHover
        responsive
        persistTableHead
      />
    </div>
  );
};

export default withAuthorization(IcarusAccounts, 31);
